/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Flex,
    SimpleGrid,
    Button,
    useMultiStyleConfig,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useContentAsset} from '../../hooks/use-content-asset'
import {useBraze} from '../../hooks/use-braze'

import Subscribe from './subscribe'
import FormatedContentAsset from '../../pages/content-detail/formated-content-asset'
import ScrollToTop from '../scroll-to-top'

const Footer = (props) => {
    const {openCountryModal} = props
    const isDesktop = useBreakpointValue({base: false, md: false, lg: true})
    const styles = useMultiStyleConfig('Footer')
    const {locale} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    useBraze(customer?.customerId)

    const footerContentAssetIds = ['footer-social-icons', 'footer-all-links-onetrust']
    const contentAssets = useContentAsset(footerContentAssetIds, locale?.id)
    const socialIconsContent = useMemo(() => {
        return contentAssets?.['footer-social-icons']?.body || ''
    }, [contentAssets])

    const footerLinksContent = useMemo(() => {
        return contentAssets?.['footer-all-links-onetrust']?.body || ''
    }, [contentAssets])

    const footerCustomClass = customer?.isRegistered ? 'customer-registered' : 'customer-guest'

    return (
        <Box {...styles.footerMainBlock}>
            <Flex className={'footer'} {...styles.footer}>
                <SimpleGrid {...styles.footerContentBlock}>
                    <Flex className={'footer-subscribe-section'}>
                        <Box className={'footer-email'} {...styles.footerEmail}>
                            {/*  Braze banner container */}
                            <Box id="global-banner-container"></Box>
                            {socialIconsContent && (
                                <Subscribe socialIconsContent={socialIconsContent} />
                            )}
                        </Box>
                    </Flex>

                    <Box
                        className={`footer-links-section ${footerCustomClass}`}
                        {...styles.footerLinksSection}
                        width={isDesktop ? '70%' : '100%'}
                        padding={{base: '0', sm: '0 16px', md: '0'}}
                    >
                        <FormatedContentAsset content={footerLinksContent} />
                    </Box>
                </SimpleGrid>
                <Flex className="country_footer" {...styles.countryFooter}>
                    <Box {...styles.countrySelector}>
                        <Button
                            onClick={openCountryModal}
                            variant="unstyled"
                            type="button"
                            backgroundImage={`url(${getAssetUrl('static/img/globe-icon.svg')})`}
                            {...styles.changeRegionBtn}
                        >
                            <FormattedMessage defaultMessage="Change Region" />
                        </Button>
                    </Box>
                    <Box {...styles.footerCopyright}>
                        <FormattedMessage defaultMessage="Copyright" />
                        <> &copy; {new Date().getFullYear()}</>
                        <FormattedMessage defaultMessage=", Crunchyroll LLC" />
                    </Box>
                </Flex>
                <ScrollToTop />
            </Flex>
        </Box>
    )
}

Footer.propTypes = {
    openCountryModal: PropTypes.func
}

export default Footer
